// src/pages/BrandHome.jsx
//
// ODFTrades — public brand homepage at "/".
// This is the FIRST thing visitors see. It positions ODFTrades as a
// trading performance brand/company, NOT as a single SaaS tool.
// The Performance Lab (the existing platform) is one product area
// inside the brand, reachable via the "Performance Lab" CTA → which
// then sits behind the beta gate.
//
// Design language reuses everything from Landing.jsx: HeroCanvas,
// HeroAtmosphere, Reveal scroll fades, glass panels, magnetic CTAs.
// No emojis. No private-beta wording on this page.
//
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import ODFLogo from '../components/ODFLogo';
import LanguageSelector from '../components/LanguageSelector';
import HeroCanvas from '../components/HeroCanvas';
import HeroAtmosphere from '../components/HeroAtmosphere';
import SectionDivider from '../components/SectionDivider';
import CookieBanner from '../components/CookieBanner';
import Spotlight from '../components/ace/Spotlight';
import { t } from '../lib/i18n';

// 3D hero centerpiece — code-split so initial paint isn't blocked
const Hero3D = lazy(() => import('../components/Hero3D'));

// Shared 3D-tilt mouse handler — used on both Pillar and Ecosystem cards
const handleTiltMove = (e) => {
  const card = e.currentTarget;
  const r = card.getBoundingClientRect();
  const x = e.clientX - r.left;
  const y = e.clientY - r.top;
  card.style.setProperty('--mx', `${x}px`);
  card.style.setProperty('--my', `${y}px`);
  card.style.setProperty('--rx', `${-((y - r.height / 2) / (r.height / 2)) * 4}deg`);
  card.style.setProperty('--ry', `${((x - r.width / 2) / (r.width / 2)) * 5}deg`);
};
const handleTiltLeave = (e) => {
  e.currentTarget.style.setProperty('--rx', '0deg');
  e.currentTarget.style.setProperty('--ry', '0deg');
};
import { useCountUp } from '../hooks/useAnimations';
import {
  ArrowRight, Compass, BarChart3, Brain, LineChart, ShieldCheck,
  Layers, BookOpen, GraduationCap, Network, Target, FileText,
  Plus, TrendingUp, Wrench, Users, Newspaper, Globe, Sparkles,
  Menu, X,
} from 'lucide-react';

const G = '#00e5a0';

// ── Scroll-fade Reveal (mirrors Landing.jsx pattern) ──────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Stat number that counts up on view ────────────────────────────
function StatNumber({ value, suffix = '' }) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  const [ref, current] = useCountUp(isNaN(n) ? 0 : n, { duration: 1200 });
  if (isNaN(n)) return <span>{value}{suffix}</span>;
  const decimals = (String(value).split('.')[1] || '').length;
  const display = decimals > 0 ? current.toFixed(decimals) : Math.round(current).toString();
  return <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>{display}{suffix}</span>;
}

// ── FAQ accordion item ────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #1a1a26' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          padding: '22px 0', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, outline: 'none',
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 500, color: '#f0f0f5', lineHeight: 1.5 }}>{q}</span>
        <span
          style={{
            width: 26, height: 26, borderRadius: '50%',
            background: open ? 'rgba(0,229,160,0.15)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${open ? 'rgba(0,229,160,0.3)' : '#2a2a3e'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: open ? G : '#8888a8',
            transform: open ? 'rotate(45deg)' : 'rotate(0)',
            transition: 'all 0.25s ease', flexShrink: 0,
          }}
        >
          <Plus size={14} strokeWidth={2} />
        </span>
      </button>
      <div style={{ maxHeight: open ? 600 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.16,1,0.3,1)' }}>
        <p style={{ fontSize: 14, color: '#8888a8', lineHeight: 1.85, paddingBottom: 22, margin: 0 }}>{a}</p>
      </div>
    </div>
  );
}

// ── FAQ data ──────────────────────────────────────────────────────
// Organised in a natural reading flow for anyone landing on the page:
// who/what ODFTrades is → trading basics for newcomers → the platform
// → what's coming → trust & legal.
const FAQS = [
  // ── About ODFTrades ─────────────────────────────────────────────
  {
    q: 'What is ODFTrades?',
    a: 'ODFTrades is an educational trading platform and software company founded by ODF. It brings together trading resources, market-learning content, journaling, analytics, and practical software tools for traders who want structure, clarity, and long-term growth.',
  },
  {
    q: 'Is ODFTrades a company or a platform?',
    a: 'Both. ODFTrades Inc. is the registered corporation operating as an educational trading and software business. ODFTrades is also the brand and digital platform itself. Performance Lab and Trading Resources are parts of the broader ODFTrades ecosystem.',
  },
  {
    q: 'Who is ODF?',
    a: 'ODF — also known as Olanrewaju Daniel Fajoyomi — is the founder of ODFTrades. ODF has been in the markets since 2019 across forex, futures, crypto, and stocks.',
  },

  // ── About Trading (for newcomers) ───────────────────────────────
  {
    q: 'I’ve never traded before — is ODFTrades for me?',
    a: 'Yes. ODFTrades is built for Traders at every stage, including people just starting to learn. The Trading Resources area is the right starting point — practical material on what trading is, how markets work, and how to approach the journey seriously. The platform tools are there when you’re ready to use them.',
  },
  {
    q: 'Is trading really a way to make money, or is it a scam?',
    a: 'Trading is a real profession — banks, hedge funds, prop firms, and individual Traders all participate in the markets. It is also one of the most misrepresented spaces on the internet, full of "get rich quick" claims that are scams. Real trading is a craft: study, screen time, risk management, and consistency over time. ODFTrades is built around that view — not the hype.',
  },
  {
    q: 'What markets does ODFTrades cover?',
    a: 'Forex (major and minor pairs), futures (NQ, ES, GC, CL and more), cryptocurrencies, stocks, and indices. The tools and resources are designed to support Traders across asset classes.',
  },

  // ── About the Platform ──────────────────────────────────────────
  {
    q: 'What is Performance Lab?',
    a: 'Performance Lab is the main trading performance product built by ODFTrades. It helps traders journal trades, import or record trading activity, review performance, identify behavioural patterns, track mistakes, measure execution, analyze results, and turn raw trading data into clearer rules for improvement. It covers the full review workflow: journaling, dashboard analytics, ODF Score grading, pattern detection, discipline tracking, risk management, calendar views, live charts, and shareable dashboard analytics. Performance Lab will stay in private beta for a while as ODFTrades grows — being used live to refine the flow and decide what ships at public release. Access is currently founder-only via the beta gate.',
  },
  {
    q: 'Is Performance Lab free to use?',
    a: 'Yes. Performance Lab is free for all core features — trade journal, dashboard, ODF Score, risk manager, live charts, calendar, CSV import/export, and shareable dashboard analytics. Pro features (Performance Coach, deeper Diagnostic, Improvement Plan) are planned as paid add-ons.',
  },

  // ── About what's coming ─────────────────────────────────────────
  {
    q: 'Will there be courses or mentorship available?',
    a: 'There are no courses or mentorship offers planned right now — the focus is on building the platform and resources. If you’re interested in courses or mentorship in the future, reach out through the contact form. As demand grows, structured education and mentorship may be added under ODFTrades.',
  },
  {
    q: 'How do I stay updated on what ODFTrades is building?',
    a: 'Follow ODFTrades on Instagram at @odftrades for updates, founder posts, and announcements as the platform grows.',
  },

  // ── Trust & legal ───────────────────────────────────────────────
  {
    q: 'Is ODFTrades a financial advisor or signal service?',
    a: 'No. ODFTrades is not a financial advisor, broker, or signal service. Performance Lab is a self-review tool. Trading Resources and any market notes shared are educational and informational only. Nothing on ODFTrades is financial advice or a recommendation to buy or sell anything. All trading decisions are yours alone — consult a licensed professional in your jurisdiction if you need advice.',
  },
];

// ── Brand pillars data ────────────────────────────────────────────
// Broad platform/company pillars. Not tool-specific. Reflects the
// "educational trading platform & software company" positioning.
const PILLARS = [
  { Icon: TrendingUp,    title: 'Trading',           desc: 'Market study, trade ideas, and lessons drawn from real time in the markets.' },
  { Icon: BookOpen,      title: 'Resources',         desc: 'PDFs, guides, market notes, and educational material for trader development.' },
  { Icon: GraduationCap, title: 'Learning',          desc: 'Structured market-learning content — built for traders moving toward consistency.' },
  { Icon: Wrench,        title: 'Tools',             desc: 'Practical trading software to journal, analyze, and improve. Performance Lab is the first.' },
  { Icon: Sparkles,      title: 'Founder Insights',  desc: 'Updates, market notes, and dashboard insights from ODF as the platform grows.' },
];

// ── Ecosystem cards ──────────────────────────────────────────────
// Three core areas. Anything more specific (ODF Score, CSV import,
// MT4/MT5, AI trade review, etc.) lives inside Performance Lab itself.
const ECOSYSTEM = [
  { Icon: Layers,    title: 'Performance Lab',   desc: 'The main trading performance product inside ODFTrades. Journal trades, review execution, track mistakes, identify behavioural patterns, and turn raw trading data into clearer rules for improvement.', status: 'live' },
  { Icon: BookOpen,  title: 'Trading Resources', desc: 'PDFs, guides, and market-learning content for traders. Starting with the first guide, with more on the way.', status: 'live' },
  { Icon: Newspaper, title: 'Market Resources',  desc: 'Market study, trade breakdowns, written guides, and learning material — shared from ODF as the brand grows.', status: 'soon' },
];

// ── Component ─────────────────────────────────────────────────────
export default function BrandHome({ onPerformanceLab, onContact, onPrivacy, onTerms, onDisclaimer, onTradingResources = () => {}, lang = 'en', setLang = () => {} }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Lock body scroll while the mobile nav is open
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [mobileNavOpen]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Magnetic CTA: cursor-tracked light pool
  const handleCtaMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <div style={{ background: '#0a0a0f', color: '#f0f0f5', fontFamily: "'IBM Plex Sans',sans-serif", overflowX: 'hidden' }}>

      {/* ── NAV ─────────────────────────────────────────────── */}
      <nav
        className="landing-nav"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          background: scrolled ? 'rgba(10,10,15,0.96)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid #1a1a26' : '1px solid transparent',
          transition: 'all 0.3s ease',
          padding: '0 32px', height: 62,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
          <ODFLogo size="compact" />
          <div className="landing-nav-links" style={{ display: 'flex', gap: 24 }}>
            {[
              ['home',       t(lang, 'nav_home'),              () => window.scrollTo({ top: 0, behavior: 'smooth' })],
              ['lab',        t(lang, 'nav_performanceLab'),    onPerformanceLab],
              ['resources',  t(lang, 'nav_tradingResources'),  onTradingResources],
              ['about',      t(lang, 'nav_aboutOdf'),          () => scrollToId('about')],
              ['contact',    t(lang, 'nav_contact'),           onContact],
            ].map(([key, label, fn]) => (
              <button
                key={key}
                onClick={fn}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: '#8888a8', padding: 0,
                  transition: 'color 0.2s', fontFamily: "'IBM Plex Sans',sans-serif",
                }}
                onMouseEnter={e => e.target.style.color = '#f0f0f5'}
                onMouseLeave={e => e.target.style.color = '#8888a8'}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LanguageSelector lang={lang} setLang={setLang} />
          <button
            className="cta-magnetic landing-nav-cta"
            onClick={onPerformanceLab}
            onMouseMove={handleCtaMove}
            style={{
              background: G, color: '#0a0a0f', border: 'none',
              borderRadius: 8, padding: '9px 20px',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans',sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }}
          >
            {t(lang, 'nav_enterPerformanceLab')} <ArrowRight size={13} />
          </button>
          {/* Hamburger — visibility controlled purely by the
              .landing-nav-burger CSS class so the media query
              can't be overridden by an inline display style. */}
          <button
            type="button"
            className="landing-nav-burger"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu size={18} />
          </button>
        </div>
      </nav>

      {/* ── MOBILE NAV OVERLAY ──────────────────────────────── */}
      {mobileNavOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(10,10,15,0.96)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            display: 'flex', flexDirection: 'column',
            padding: '22px 22px 36px',
            animation: 'fadeIn 0.22s ease',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 36 }}>
            <ODFLogo size="compact" />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
              style={{
                width: 40, height: 40, padding: 0,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid #2a2a3e', borderRadius: 9,
                cursor: 'pointer', color: '#f0f0f5',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={18} />
            </button>
          </div>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            {[
              [t(lang, 'nav_home'),              () => { setMobileNavOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }],
              [t(lang, 'nav_performanceLab'),    () => { setMobileNavOpen(false); onPerformanceLab(); }],
              [t(lang, 'nav_tradingResources'),  () => { setMobileNavOpen(false); onTradingResources(); }],
              [t(lang, 'nav_aboutOdf'),          () => { setMobileNavOpen(false); scrollToId('about'); }],
              [t(lang, 'nav_contact'),           () => { setMobileNavOpen(false); onContact(); }],
            ].map(([label, fn]) => (
              <button
                key={label}
                onClick={fn}
                style={{
                  textAlign: 'left',
                  background: 'none', border: 'none',
                  padding: '18px 4px',
                  fontSize: 22, fontWeight: 600,
                  color: '#f0f0f5', letterSpacing: '-0.02em',
                  cursor: 'pointer',
                  borderBottom: '1px solid #1a1a26',
                  fontFamily: "'Geist','IBM Plex Sans',sans-serif",
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span>{label}</span>
                <ArrowRight size={18} style={{ color: '#55556a' }} />
              </button>
            ))}
          </nav>

          {/* Bottom CTA removed per request — Performance Lab is
              reachable from the nav item above. */}
        </div>
      )}

      {/* ── HERO ────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="landing-hero"
        style={{
          minHeight: '100vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center',
          padding: '120px 24px 80px',
          position: 'relative', overflow: 'hidden',
        }}
      >
        <HeroCanvas />
        <HeroAtmosphere />

        <Reveal style={{ position: 'relative', zIndex: 2 }}>
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(0,229,160,0.07)',
              border: '1px solid rgba(0,229,160,0.22)',
              borderRadius: 100, padding: '6px 16px', marginBottom: 30,
            }}
          >
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: G, animation: 'pulse 2s infinite' }} />
            <span
              style={{
                fontSize: 11, color: G,
                fontFamily: "'DM Mono',monospace",
                letterSpacing: '0.12em', textTransform: 'uppercase',
              }}
            >
              {t(lang, 'brand_pillTagline')}
            </span>
          </div>
        </Reveal>

        {/* ── 3D HERO CENTERPIECE ─────────────────────────────
            Lazy-loaded WebGL scene. Suspense fallback keeps the
            same vertical footprint so the layout doesn't jump. */}
        <Reveal delay={0.06} style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 880, margin: '0 auto' }}>
          <Suspense
            fallback={
              <div
                aria-hidden
                style={{
                  width: '100%', height: 380,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#55556a',
                  fontFamily: "'DM Mono',monospace", fontSize: 10, letterSpacing: '0.16em',
                }}
              >
                INITIALISING ORBITAL CORE…
              </div>
            }
          >
            <Hero3D height={380} />
          </Suspense>
        </Reveal>

        {/* Hero H1 removed for now (per latest brief). The eyebrow chip above
            already carries the "all-in-one trading platform" message; the
            subhead below speaks to what's inside. Big H1 can be re-added later. */}

        <Reveal delay={0.12} style={{ position: 'relative', zIndex: 2 }}>
          <p
            style={{
              fontSize: 17, color: '#8888a8',
              maxWidth: 720, margin: '20px auto 0',
              lineHeight: 1.85,
            }}
          >
            {/* Subhead is translated as a whole sentence; the
                STANDALONE token "ODF" inside the translated string
                is wrapped in green so the founder reference pops.
                The negative lookahead skips "ODF" when it is the
                start of "ODFTrades" (or any other ODF-prefixed
                word) so the brand name stays in normal copy text. */}
            {(() => {
              const txt = t(lang, 'brand_heroSubhead');
              const re = /ODF(?![A-Za-z])/g;
              const out = [];
              let last = 0;
              let m;
              while ((m = re.exec(txt)) !== null) {
                if (m.index > last) out.push(txt.slice(last, m.index));
                out.push(
                  <span key={`hl-${m.index}`} style={{ color: G, fontWeight: 600 }}>ODF</span>
                );
                last = re.lastIndex;
              }
              if (last < txt.length) out.push(txt.slice(last));
              return out;
            })()}
          </p>
        </Reveal>

        {/* Hero CTAs removed — "Enter Performance Lab" already sits in the top-right nav. */}

      </section>

      <SectionDivider variant="curve" />

      {/* ── BRAND PILLARS ──────────────────────────────────────── */}
      <section id="brand" style={{ padding: '100px 24px', maxWidth: 1080, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 18 }}>
            What ODFTrades Is
          </div>
          <h2 style={{ fontSize: 'clamp(30px, 4.4vw, 52px)', fontWeight: 600, letterSpacing: '-0.03em', margin: '0 0 22px', lineHeight: 1.15 }}>
            The brand. The platform.
            <br />
            <span style={{ color: G }}>One place to grow as a trader.</span>
          </h2>
          <p style={{ fontSize: 15.5, color: '#8888a8', maxWidth: 660, margin: '0 auto', lineHeight: 1.9 }}>
            ODFTrades is an educational trading platform and software company founded by ODF — bringing
            together trading resources, market-learning content, journaling, analytics, and practical
            software for traders who want structure, clarity, and long-term growth.
          </p>
        </Reveal>

        {/* ── BENTO GRID ───────────────────────────────────────
            Asymmetric layout, Apple/Linear/Stripe-style. First
            pillar (Trading) is the feature tile spanning 2×2.
            On phones it collapses to a single column. */}
        <div className="pillar-bento">
          {PILLARS.map((p, i) => {
            const featured = i === 0; // Trading
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className={`card-magnetic pillar-tile ${featured ? 'pillar-tile--featured' : ''}`}
                  onMouseMove={handleTiltMove}
                  onMouseLeave={handleTiltLeave}
                  style={{
                    background: featured
                      ? 'linear-gradient(155deg, #0d0d14 0%, #0d0d14 55%, rgba(0,229,160,0.04) 100%)'
                      : '#0d0d14',
                    border: '1px solid #1a1a26',
                    borderRadius: 16,
                    padding: featured ? '34px 28px' : '24px 22px',
                    height: '100%',
                    boxSizing: 'border-box',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                  }}
                >
                  {/* top accent stripe */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0, left: 18, right: 18, height: 1,
                      background: `linear-gradient(90deg, transparent, ${G}66, transparent)`,
                    }}
                  />
                  {/* subtle corner glow on the featured tile */}
                  {featured && (
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        right: -40, bottom: -40,
                        width: 220, height: 220,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(0,229,160,0.10), transparent 65%)',
                        filter: 'blur(8px)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <div
                    style={{
                      width: featured ? 50 : 38,
                      height: featured ? 50 : 38,
                      borderRadius: featured ? 14 : 10,
                      background: 'rgba(0,229,160,0.08)',
                      border: '1px solid rgba(0,229,160,0.18)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: G,
                      marginBottom: featured ? 22 : 14,
                    }}
                  >
                    <p.Icon size={featured ? 22 : 17} strokeWidth={1.7} />
                  </div>
                  <div
                    style={{
                      fontSize: featured ? 22 : 14,
                      fontWeight: 600,
                      color: '#f0f0f5',
                      marginBottom: featured ? 10 : 8,
                      letterSpacing: featured ? '-0.018em' : '0',
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{
                      fontSize: featured ? 15 : 12.5,
                      color: '#8888a8',
                      lineHeight: featured ? 1.75 : 1.7,
                      maxWidth: featured ? 360 : 'none',
                    }}
                  >
                    {p.desc}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <SectionDivider variant="line" />

      {/* ── ECOSYSTEM FEATURES (now appears FIRST — Performance Lab is one card among many) ───── */}
      <section id="ecosystem" style={{ padding: '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14 }}>
            The Ecosystem
          </div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 600, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.15 }}>
            What ODFTrades Is Building.
          </h2>
          <p style={{ fontSize: 15, color: '#8888a8', maxWidth: 600, margin: '0 auto', lineHeight: 1.85 }}>
            Tools, resources, education, market study, and products — gathered inside one trader-led platform.
            Performance Lab is the first major product. More is being built around it.
          </p>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {ECOSYSTEM.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div
                className="card-magnetic"
                style={{
                  background: '#0d0d14',
                  border: '1px solid #1a1a26',
                  borderRadius: 14,
                  padding: '24px 22px',
                  height: '100%', boxSizing: 'border-box',
                  position: 'relative', overflow: 'hidden',
                }}
                onMouseMove={(e) => {
                  const card = e.currentTarget;
                  const r = card.getBoundingClientRect();
                  const x = e.clientX - r.left;
                  const y = e.clientY - r.top;
                  card.style.setProperty('--mx', `${x}px`);
                  card.style.setProperty('--my', `${y}px`);
                  card.style.setProperty('--rx', `${-((y - r.height / 2) / (r.height / 2)) * 4}deg`);
                  card.style.setProperty('--ry', `${((x - r.width / 2) / (r.width / 2)) * 5}deg`);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.setProperty('--rx', '0deg');
                  e.currentTarget.style.setProperty('--ry', '0deg');
                }}
              >
                <div
                  style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: 'rgba(0,229,160,0.08)',
                    border: '1px solid rgba(0,229,160,0.2)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    color: G, marginBottom: 14,
                  }}
                >
                  <f.Icon size={18} strokeWidth={1.7} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#f0f0f5' }}>{f.title}</div>
                  {f.status === 'soon' && (
                    <span
                      style={{
                        fontSize: 9,
                        color: '#8888a8',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid #2a2a3e',
                        borderRadius: 100,
                        padding: '2px 8px',
                        fontFamily: "'DM Mono',monospace",
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: '#8888a8', lineHeight: 1.7 }}>{f.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── PERFORMANCE LAB — small secondary callout, not the centerpiece ── */}
      <section id="performance-lab" style={{ padding: '40px 24px 60px', maxWidth: 880, margin: '0 auto' }}>
        <Reveal>
          <Spotlight color={G} size={520} intensity={0.16} style={{ borderRadius: 16 }}>
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(0,229,160,0.05) 0%, rgba(76,201,240,0.025) 100%)',
              border: '1px solid rgba(0,229,160,0.18)',
              borderRadius: 16,
              padding: '32px 32px',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 28,
              alignItems: 'center',
              position: 'relative', overflow: 'hidden',
            }}
            className="lab-callout"
          >
            <div style={{ position: 'absolute', top: 0, left: 32, right: 32, height: 1, background: `linear-gradient(90deg, transparent, ${G}55, transparent)` }} />
            <div>
              <div style={{ fontSize: 10, color: G, fontFamily: "'DM Mono',monospace", letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
                First Product Inside ODFTrades
              </div>
              <h3
                style={{
                  fontSize: 'clamp(18px, 2.4vw, 22px)',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  margin: '0 0 6px',
                  lineHeight: 1.25,
                }}
              >
                ODFTRADES TRADING PERFORMANCE SYSTEM
              </h3>
              <p
                style={{
                  fontSize: 13, color: G,
                  fontWeight: 500, lineHeight: 1.5,
                  margin: '0 0 12px',
                  fontFamily: "'IBM Plex Sans',sans-serif",
                }}
              >
                Trade Smarter. Analyze Deeper. Build Discipline.
              </p>
              <p
                style={{
                  fontSize: 13, color: '#8888a8',
                  margin: 0, lineHeight: 1.7,
                }}
              >
                The main trading performance product inside ODFTrades — journaling, trade tracking,
                performance analytics, execution review, behavioural pattern detection, mistake tracking,
                discipline building, and data-driven review for trader development.
              </p>
            </div>
            <button
              className="cta-magnetic lab-callout-btn"
              onClick={onPerformanceLab}
              onMouseMove={handleCtaMove}
              style={{
                background: G, color: '#0a0a0f', border: 'none',
                borderRadius: 9, padding: '13px 22px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'IBM Plex Sans',sans-serif",
                display: 'inline-flex', alignItems: 'center', gap: 8,
                whiteSpace: 'nowrap',
              }}
            >
              Enter Performance Lab <ArrowRight size={14} />
            </button>
          </div>
          </Spotlight>
        </Reveal>
      </section>

      <SectionDivider variant="curve" />

      {/* ── ABOUT ODF ──────────────────────────────────────────── */}
      <section
        id="about"
        style={{ padding: '100px 24px', maxWidth: 820, margin: '0 auto', textAlign: 'center' }}
      >
        <Reveal>
          <div
            style={{
              width: 78, height: 78, borderRadius: '50%',
              background: 'rgba(0,229,160,0.08)',
              border: '2px solid rgba(0,229,160,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <span
              style={{
                fontSize: 20, fontWeight: 700, color: G,
                fontFamily: "'DM Mono',monospace", letterSpacing: '0.04em',
              }}
            >
              ODF
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14 }}>
            Founder · ODF
          </div>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 600, letterSpacing: '-0.025em', margin: '0 0 20px', lineHeight: 1.25, color: '#f0f0f5' }}>
            The Trader Behind ODFTrades.
          </h2>
          <p style={{ fontSize: 15.5, color: '#8888a8', lineHeight: 1.9, margin: '0 0 18px' }}>
            ODF, also known as Olanrewaju Daniel Fajoyomi, is the founder of ODFTrades. ODF is a trader who has been in the markets since 2019 across forex,
            futures, crypto, and stocks.
          </p>
          <p style={{ fontSize: 15.5, color: '#8888a8', lineHeight: 1.9, margin: '0 0 18px' }}>
            ODFTrades was built from years of studying the markets, taking trades, learning from
            mistakes, and building systems for consistency. The brand is being built from the perspective
            of a trader still growing, still improving, and creating the educational content and software
            tools he wishes he had earlier in the journey.
          </p>
          <p style={{ fontSize: 14.5, color: '#8888a8', lineHeight: 1.85, margin: '0 0 18px' }}>
            As ODFTrades grows, the platform may also give traders access to ODF's market notes, trade
            updates, and live dashboard insights — creating a more transparent way to follow the journey
            behind the brand.
          </p>
          <p style={{ fontSize: 13.5, color: '#55556a', lineHeight: 1.85, fontStyle: 'italic' }}>
            Educational and informational only. Not financial advice. Not guaranteed outcomes.
          </p>
        </Reveal>
      </section>

      <SectionDivider variant="line" />

      {/* Trading Resources moved to its own page at /trading-resources — accessed via the nav. */}

      <SectionDivider variant="curve" />

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section id="faq" style={{ padding: '100px 24px', maxWidth: 780, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14 }}>
            FAQ
          </div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>
            About ODFTrades.
          </h2>
        </Reveal>
        <div className="landing-faq">
          {FAQS.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)}
        </div>
      </section>

      {/* ── CONTACT TEASER ────────────────────────────────────── */}
      <section style={{ padding: '80px 24px', maxWidth: 780, margin: '0 auto', textAlign: 'center' }}>
        <Reveal>
          <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14 }}>
            Get in Touch
          </div>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 38px)', fontWeight: 600, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.2 }}>
            Reach Out.
          </h2>
          <p style={{ fontSize: 14.5, color: '#8888a8', margin: '0 auto 28px', lineHeight: 1.85, maxWidth: 480 }}>
            Questions, suggestions, partnerships, or feedback. The brand is built in the open and listens to
            Traders.
          </p>
          <button
            onClick={onContact}
            style={{
              background: 'transparent', color: G,
              border: '1px solid rgba(0,229,160,0.3)', borderRadius: 9,
              padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans',sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: 8,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,229,160,0.07)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            Contact ODFTrades <ArrowRight size={14} />
          </button>
        </Reveal>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────── */}
      <footer
        style={{
          borderTop: '1px solid #1a1a26',
          padding: '36px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 18,
        }}
      >
        <ODFLogo size="compact" />
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
          {[
            ['Contact',    onContact],
            ['Privacy',    onPrivacy],
            ['Terms',      onTerms],
            ['Disclaimer', onDisclaimer],
          ].map(([label, fn]) => (
            <button
              key={label}
              onClick={fn}
              style={{
                background: 'none', border: 'none', color: '#55556a',
                fontSize: 12, cursor: 'pointer',
                fontFamily: "'IBM Plex Sans',sans-serif",
                transition: 'color 0.2s', padding: 0,
              }}
              onMouseEnter={e => e.target.style.color = '#f0f0f5'}
              onMouseLeave={e => e.target.style.color = '#55556a'}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#2a2a3e', fontFamily: "'DM Mono',monospace" }}>
          © {new Date().getFullYear()} ODFTrades Inc. · Educational trading platform & software company
        </div>
      </footer>

      <CookieBanner />

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

        /* ── BENTO GRID for Brand Pillars ──────────────────
           Asymmetric: featured tile (first child) spans 2 cols
           × 2 rows. The remaining four tiles each span 2 cols
           × 1 row, flowing around the feature. Rules target the
           direct child (a Reveal wrapper) because the .card lives
           one layer deeper. */
        .pillar-bento {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-auto-rows: minmax(170px, auto);
          gap: 16px;
        }
        .pillar-bento > * { grid-column: span 2; min-width: 0; }
        .pillar-bento > *:first-child { grid-column: span 2; grid-row: span 2; }
        .pillar-bento .pillar-tile { height: 100%; }

        @media (max-width: 900px) {
          .pillar-bento { grid-template-columns: repeat(2, 1fr); grid-auto-rows: auto; }
          .pillar-bento > * { grid-column: span 1; }
          .pillar-bento > *:first-child { grid-column: span 2; grid-row: auto; }
        }
        @media (max-width: 560px) {
          .pillar-bento { grid-template-columns: 1fr; }
          .pillar-bento > *,
          .pillar-bento > *:first-child { grid-column: span 1; grid-row: auto; }
        }

        @media (max-width: 768px) {
          section { padding: 60px 20px !important; }
        }
        /* ── Hamburger button — default state ───────────────
           Hidden on desktop. Made visible on mobile by the
           media query below. Pure CSS so it can never be
           clobbered by an inline display style. */
        .landing-nav-burger {
          display: none;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: rgba(255,255,255,0.04);
          border: 1px solid #2a2a3e;
          border-radius: 9px;
          cursor: pointer;
          color: #f0f0f5;
        }
        .landing-nav-burger:hover {
          border-color: rgba(0,229,160,0.4);
          background: rgba(0,229,160,0.06);
        }

        @media (max-width: 768px) {
          .landing-nav-links { display: none !important; }
          .landing-nav      { padding: 0 16px !important; }
          .lab-callout      { grid-template-columns: 1fr !important; padding: 26px 22px !important; }
          .lab-callout-btn  { width: 100%; justify-content: center; }

          /* Mobile nav: hide the big green top CTA, show the
             hamburger. The hamburger overlay carries every nav
             link — including Performance Lab — so the CTA is
             not needed inline. */
          .landing-nav-cta    { display: none !important; }
          .landing-nav-burger { display: inline-flex; }
        }
      `}</style>
    </div>
  );
}
